import { useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";

import { AuthContext } from "../app/authContext";
import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import { ApiError } from "../services/http";
import {
  copySldFromPrevious,
  getSld,
  saveSld,
  sldModelRequestSchema,
  StructuralSaveError,
  type SldDocument,
  type StructuralError,
} from "../services/sld";
import {
  busKindSchema,
  deviceKindSchema,
  feederKindSchema,
  loadKindSchema,
  sourceKindSchema,
} from "../services/sldContract";
import {
  BUS_KIND_LABELS,
  buildSldModel,
  createInitialSldDraft,
  DEVICE_KIND_LABELS,
  draftFromDocument,
  FEEDER_KIND_LABELS,
  isStale,
  LOAD_KIND_LABELS,
  newBusRow,
  newFeederRow,
  newIncomerRow,
  newLoadRow,
  newSourceRow,
  newTransformerRow,
  nextRowId,
  SLD_LABELS,
  sldDraftModule,
  sldDraftSchema,
  SOURCE_KIND_LABELS,
  TARGET_KIND_LABELS,
  type BusRow,
  type CableDraft,
  type DeviceDraft,
  type FeederRow,
  type IncomerRow,
  type LoadRow,
  type SldDraft,
  type SldRowKind,
  type SldTargetKind,
  type SourceRow,
  type TransformerRow,
} from "../sld/sldDraft";
import {
  describeLocation,
  describeValidationIssue,
  type ValidationIssueLike,
} from "../utils/validationMessages";
import { SldExport } from "./SldExport";
import { SldPreview } from "./SldPreview";
import { SldValidationPanel } from "./SldValidationPanel";

/** The first option of every choice and every picker: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

/** One shared empty list, so the panel and the picture see the same props between saves. */
const NO_ERRORS: readonly StructuralError[] = [];

export const NO_REVISION_NOTE =
  "Select a project revision to edit its single-line diagram.";
export const READ_ONLY_NOTE = "This revision is read-only.";
export const STALE_NOTE =
  "The diagram was changed since your draft started; reload it to continue.";
export const LOADING_NOTE = "Loading the diagram…";

type SldEditorFormProps = {
  /** Called with the draft after every change, for a live preview of the diagram. */
  onDraft?: (draft: SldDraft) => void;
  /** Called with the stored document after a save or a copy. */
  onSaved?: (document: SldDocument) => void;
};

// The single-line diagram editor (SLD-a, A26 (f)). One diagram per project revision: the
// form loads the revision's stored document, keeps the engineer's draft in the tab under a
// key that carries the revision id, and saves the whole model against the version the
// draft started from (baseVersion). The server refuses a save that breaks a structural rule
// with every error at once; the form lists them and marks the rows they name. A save
// against an older version, or into a revision that is no longer open, is a conflict the
// engineer resolves by reloading. A viewer, or a revision that is not OPEN, sees the
// diagram read-only. A revision without a diagram may start from the previous revision's.
// Below the form sit the validation panel (SldValidationPanel: the saved check, its
// warnings and the structural errors of the last refused save, each item leading to its
// row), the export of the saved version (SldExport: SVG download and print) and the
// diagram drawn live from the draft (SldPreview: a short pause after the last keystroke,
// re-rendering the picture alone), with the same errors and warnings marked.
export function SldEditorForm({ onDraft, onSaved }: SldEditorFormProps) {
  const { state } = useProject();
  // Read without useAuth on purpose: a form rendered outside the AuthProvider (in a test)
  // has no user, exactly like a signed-out visitor.
  const auth = useContext(AuthContext);
  const role = auth?.state.status === "signed-in" ? auth.state.session.role : null;

  if (state.status !== "selected" && state.status !== "read-only") {
    return (
      <form aria-label="Single-line diagram editor">
        <p data-no-revision-note="true">{NO_REVISION_NOTE}</p>
      </form>
    );
  }

  // Every piece of state belongs to one revision: a new revision mounts a fresh editor.
  return (
    <SldEditor
      key={state.revision.id}
      projectId={state.project.id}
      revisionId={state.revision.id}
      project={{ code: state.project.code, name: state.project.name }}
      revision={{
        revision_number: state.revision.revision_number,
        label: state.revision.label,
        status: state.revision.status,
      }}
      revisionOpen={state.revision.status === "OPEN"}
      viewer={role === "VIEWER"}
      onDraft={onDraft}
      onSaved={onSaved}
    />
  );
}

type SldEditorProps = SldEditorFormProps & {
  projectId: string;
  revisionId: string;
  /** For the export's title block and file name. */
  project: { code: string; name: string };
  revision: { revision_number: number; label: string; status: string };
  revisionOpen: boolean;
  viewer: boolean;
};

type SaveState =
  | { status: "idle" }
  | { status: "saving" }
  | { status: "saved"; version: number }
  | { status: "structure"; errors: readonly StructuralError[] }
  | { status: "conflict"; message: string }
  | { status: "error"; message: string };

function SldEditor({
  projectId,
  revisionId,
  project,
  revision,
  revisionOpen,
  viewer,
  onDraft,
  onSaved,
}: SldEditorProps) {
  const { draft, setDraft, restored } = useStudyDraft<SldDraft>({
    module: sldDraftModule(revisionId),
    createInitial: () => createInitialSldDraft(revisionId),
    schema: sldDraftSchema,
  });
  // Whether the draft came back from the tab's storage when this editor mounted: a
  // restored draft is kept and compared with the server; a fresh one is filled from it.
  const restoredAtMount = useRef(restored);

  const [document, setDocument] = useState<SldDocument | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>({ status: "idle" });
  const [copyError, setCopyError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const formElement = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoadError(null);
    getSld(projectId, revisionId, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) {
          return;
        }
        setDocument(loaded);
        if (!restoredAtMount.current) {
          setDraft(draftFromDocument(loaded));
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoadError(
            error instanceof Error ? error.message : "The diagram could not be loaded.",
          );
        }
      });
    return () => controller.abort();
  }, [projectId, revisionId, reloadCount, setDraft]);

  useEffect(() => {
    onDraft?.(draft);
  }, [draft, onDraft]);

  const readOnly = viewer || !revisionOpen || document?.revision_status !== "OPEN";
  const loading = document === null && loadError === null;
  const stale = document !== null && isStale(draft, document);
  const disabled = readOnly || loading || save.status === "saving" || copying;

  // Take the server's diagram as the new draft, base version included.
  function reloadFromServer() {
    if (document !== null) {
      setDraft(draftFromDocument(document));
    }
    restoredAtMount.current = false;
    setSave({ status: "idle" });
    setValidationError(null);
    setReloadCount((count) => count + 1);
  }

  function update(patch: Partial<SldDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function addRow(kind: SldRowKind) {
    setDraft((current) => {
      const id = nextRowId(current, kind);
      switch (kind) {
        case "bus":
          return { ...current, buses: [...current.buses, newBusRow(id)] };
        case "source":
          return { ...current, sources: [...current.sources, newSourceRow(id)] };
        case "transformer":
          return { ...current, transformers: [...current.transformers, newTransformerRow(id)] };
        case "feeder":
          return { ...current, feeders: [...current.feeders, newFeederRow(id)] };
        case "incomer":
          return { ...current, incomers: [...current.incomers, newIncomerRow(id)] };
        case "load":
          return { ...current, loads: [...current.loads, newLoadRow(id)] };
      }
    });
  }

  function rows<T extends { id: string }>(list: T[], id: string, patch: Partial<T>): T[] {
    return list.map((row) => (row.id === id ? { ...row, ...patch } : row));
  }

  function without<T extends { id: string }>(list: T[], id: string): T[] {
    return list.filter((row) => row.id !== id);
  }

  // The xor of a feeder's target reads as one missing choice; every other issue by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    if (issue.code === "custom" && issue.path.at(-1) === "to_bus") {
      return `${describeLocation(issue.path.slice(0, -1), SLD_LABELS)} — Target is required.`;
    }
    return describeValidationIssue(issue, SLD_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);
    setSave({ status: "idle" });

    const parsed = sldModelRequestSchema.safeParse(buildSldModel(draft));
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the diagram inputs.");
      return;
    }

    setSave({ status: "saving" });
    try {
      const saved = await saveSld(projectId, revisionId, parsed.data, draft.baseVersion);
      setDocument(saved);
      update({ baseVersion: saved.version });
      setSave({ status: "saved", version: saved.version });
      onSaved?.(saved);
    } catch (error: unknown) {
      if (error instanceof StructuralSaveError) {
        setSave({ status: "structure", errors: error.errors });
      } else if (error instanceof ApiError && error.status === 409) {
        setSave({ status: "conflict", message: error.message });
      } else {
        setSave({
          status: "error",
          message: error instanceof Error ? error.message : "The diagram was not saved.",
        });
      }
    }
  }

  async function handleCopy() {
    setCopyError(null);
    setCopying(true);
    try {
      const copied = await copySldFromPrevious(projectId, revisionId);
      setDocument(copied);
      setDraft(draftFromDocument(copied));
      restoredAtMount.current = false;
      setSave({ status: "saved", version: copied.version });
      onSaved?.(copied);
    } catch (error: unknown) {
      setCopyError(
        error instanceof Error ? error.message : "The previous diagram could not be copied.",
      );
    } finally {
      setCopying(false);
    }
  }

  // The codes the pickers offer, live from the draft; a code typed twice is offered once.
  const codesOf = (list: ReadonlyArray<{ code: string }>): string[] => {
    const codes: string[] = [];
    for (const row of list) {
      const code = row.code.trim();
      if (code !== "" && !codes.includes(code)) {
        codes.push(code);
      }
    }
    return codes;
  };
  const busCodes = codesOf(draft.buses);
  const sourceCodes = codesOf(draft.sources);
  const loadCodes = codesOf(draft.loads);

  // The elements the structural errors name, so their rows can be marked.
  const flaggedElements = new Set(
    save.status === "structure" ? save.errors.map((error) => error.element) : [],
  );
  const flagged = (codes: string[]): boolean =>
    codes.some((code) => flaggedElements.has(code.trim()));

  // The row that holds each code the draft names - an element, a device or a cable - so a
  // validation item can lead to it. A code typed twice leads to its first row.
  const rowIdByCode = new Map<string, string>();
  const remember = (code: string, rowId: string) => {
    const trimmed = code.trim();
    if (trimmed !== "" && !rowIdByCode.has(trimmed)) {
      rowIdByCode.set(trimmed, rowId);
    }
  };
  for (const bus of draft.buses) remember(bus.code, bus.id);
  for (const source of draft.sources) remember(source.code, source.id);
  for (const transformer of draft.transformers) {
    remember(transformer.code, transformer.id);
    remember(transformer.device.code, transformer.id);
  }
  for (const incomer of draft.incomers) {
    remember(incomer.code, incomer.id);
    remember(incomer.device.code, incomer.id);
  }
  for (const feeder of draft.feeders) {
    remember(feeder.code, feeder.id);
    remember(feeder.device.code, feeder.id);
    remember(feeder.cable.code, feeder.id);
  }
  for (const load of draft.loads) remember(load.code, load.id);

  // Scroll the row of a code into view and put the focus on its first field. Every row is
  // always on the page (the sections do not collapse), so there is nothing to open first.
  function goToRow(code: string) {
    const rowId = rowIdByCode.get(code);
    const row =
      rowId === undefined
        ? null
        : (formElement.current?.querySelector<HTMLElement>(`[data-sld-row="${rowId}"]`) ?? null);
    if (row === null) {
      return;
    }
    if (typeof row.scrollIntoView === "function") {
      // Absent in jsdom; the browser's scroll-behavior decides whether it is smooth.
      row.scrollIntoView({ block: "center" });
    }
    (row.querySelector<HTMLElement>("input, select") ?? row).focus();
  }

  return (
    <>
      <form aria-label="Single-line diagram editor" onSubmit={handleSubmit} ref={formElement}>
        {restored ? <p role="status">Draft restored from this session.</p> : null}
        {loading ? <p role="status">{LOADING_NOTE}</p> : null}
        {loadError !== null ? (
          <p role="alert">
            {loadError}{" "}
            <button type="button" onClick={reloadFromServer}>
              Reload
            </button>
          </p>
        ) : null}
        {readOnly && !loading ? <p data-read-only-note="true">{READ_ONLY_NOTE}</p> : null}
        {stale && !readOnly ? (
          <p role="alert" data-stale-note="true">
            {STALE_NOTE}{" "}
            <button type="button" onClick={reloadFromServer}>
              Reload
            </button>
          </p>
        ) : null}
        {document !== null && document.version === 0 && !readOnly ? (
          <p data-copy-from-previous="true">
            <button type="button" disabled={disabled} onClick={handleCopy}>
              Copy from previous revision
            </button>
            {copyError !== null ? <span role="alert"> {copyError}</span> : null}
          </p>
        ) : null}

        <fieldset disabled={disabled}>
          <legend>Diagram</legend>
          <label>
            Diagram code
            <input
              name="code"
              value={draft.code}
              onChange={(event) => update({ code: event.target.value })}
            />
          </label>
          <label>
            Diagram name
            <input
              name="name"
              value={draft.name}
              onChange={(event) => update({ name: event.target.value })}
            />
          </label>
          <label>
            Diagram notes
            <input
              name="notes"
              value={draft.notes}
              onChange={(event) => update({ notes: event.target.value })}
            />
          </label>
        </fieldset>

        <fieldset disabled={disabled}>
          <legend>Buses</legend>
          {draft.buses.map((bus, index) => (
            <BusFields
              key={bus.id}
              bus={bus}
              index={index}
              flagged={flagged([bus.code])}
              onChange={(patch) => update({ buses: rows(draft.buses, bus.id, patch) })}
              onRemove={() => update({ buses: without(draft.buses, bus.id) })}
            />
          ))}
          <button type="button" onClick={() => addRow("bus")}>
            Add bus
          </button>
        </fieldset>

        <fieldset disabled={disabled}>
          <legend>Sources</legend>
          {draft.sources.map((source, index) => (
            <SourceFields
              key={source.id}
              source={source}
              index={index}
              flagged={flagged([source.code])}
              onChange={(patch) => update({ sources: rows(draft.sources, source.id, patch) })}
              onRemove={() => update({ sources: without(draft.sources, source.id) })}
            />
          ))}
          <button type="button" onClick={() => addRow("source")}>
            Add source
          </button>
        </fieldset>

        <fieldset disabled={disabled}>
          <legend>Transformers</legend>
          {draft.transformers.map((transformer, index) => (
            <TransformerFields
              key={transformer.id}
              transformer={transformer}
              index={index}
              busCodes={busCodes}
              flagged={flagged([transformer.code, transformer.device.code])}
              onChange={(patch) =>
                update({ transformers: rows(draft.transformers, transformer.id, patch) })
              }
              onRemove={() => update({ transformers: without(draft.transformers, transformer.id) })}
            />
          ))}
          <button type="button" onClick={() => addRow("transformer")}>
            Add transformer
          </button>
        </fieldset>

        <fieldset disabled={disabled}>
          <legend>Incomers</legend>
          {draft.incomers.map((incomer, index) => (
            <IncomerFields
              key={incomer.id}
              incomer={incomer}
              index={index}
              busCodes={busCodes}
              sourceCodes={sourceCodes}
              flagged={flagged([incomer.code, incomer.device.code])}
              onChange={(patch) => update({ incomers: rows(draft.incomers, incomer.id, patch) })}
              onRemove={() => update({ incomers: without(draft.incomers, incomer.id) })}
            />
          ))}
          <button type="button" onClick={() => addRow("incomer")}>
            Add incomer
          </button>
        </fieldset>

        <fieldset disabled={disabled}>
          <legend>Feeders</legend>
          {draft.feeders.map((feeder, index) => (
            <FeederFields
              key={feeder.id}
              feeder={feeder}
              index={index}
              busCodes={busCodes}
              loadCodes={loadCodes}
              flagged={flagged([feeder.code, feeder.device.code, feeder.cable.code])}
              onChange={(patch) => update({ feeders: rows(draft.feeders, feeder.id, patch) })}
              onRemove={() => update({ feeders: without(draft.feeders, feeder.id) })}
            />
          ))}
          <button type="button" onClick={() => addRow("feeder")}>
            Add feeder
          </button>
        </fieldset>

        <fieldset disabled={disabled}>
          <legend>Loads</legend>
          {draft.loads.map((load, index) => (
            <LoadFields
              key={load.id}
              load={load}
              index={index}
              flagged={flagged([load.code])}
              onChange={(patch) => update({ loads: rows(draft.loads, load.id, patch) })}
              onRemove={() => update({ loads: without(draft.loads, load.id) })}
            />
          ))}
          <button type="button" onClick={() => addRow("load")}>
            Add load
          </button>
        </fieldset>

        {validationError ? <p role="alert">{validationError}</p> : null}
        {save.status === "saved" ? (
          <p role="status" data-save-state="saved">
            Saved version {save.version}.
          </p>
        ) : null}
        {save.status === "conflict" ? (
          <p role="alert" data-save-state="conflict">
            {save.message}{" "}
            <button type="button" onClick={reloadFromServer}>
              Reload
            </button>
          </p>
        ) : null}
        {save.status === "error" ? (
          <p role="alert" data-save-state="error">
            {save.message}
          </p>
        ) : null}

        {readOnly ? null : (
          <button disabled={disabled} type="submit">
            Save diagram
          </button>
        )}
      </form>
      <SldValidationPanel
        document={document}
        errors={save.status === "structure" ? save.errors : NO_ERRORS}
        hasRow={(code) => rowIdByCode.has(code)}
        onGoTo={goToRow}
      />
      <SldExport saved={document} draft={draft} project={project} revision={revision} />
      <SldPreview
        draft={draft}
        errors={save.status === "structure" ? save.errors : undefined}
        warnings={document?.check.warnings}
      />
    </>
  );
}

// -- rows ----------------------------------------------------------------------------------

type RowProps = { index: number; flagged: boolean; onRemove: () => void };

function Row({
  kind,
  id,
  index,
  flagged,
  onRemove,
  children,
}: RowProps & { kind: string; id: string; children: ReactNode }) {
  return (
    <fieldset
      data-sld-row={id}
      data-structural-error={flagged ? "true" : undefined}
      aria-invalid={flagged ? true : undefined}
    >
      <legend>
        {kind} {index + 1}
      </legend>
      {children}
      <button type="button" onClick={onRemove}>
        Remove {kind.toLowerCase()} {index + 1}
      </button>
    </fieldset>
  );
}

// A select with "Choose…" first and the contract's values after it.
function Choice<T extends string>({
  label,
  name,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  name: string;
  value: string;
  options: readonly T[];
  labels: Record<T, string>;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      {label}
      <select name={name} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );
}

// A picker of an element code from the draft, live; a code the draft no longer holds is
// still shown so the row keeps what was chosen until the engineer changes it.
function Picker({
  label,
  name,
  value,
  codes,
  onChange,
}: {
  label: string;
  name: string;
  value: string;
  codes: string[];
  onChange: (value: string) => void;
}) {
  const options = value !== "" && !codes.includes(value) ? [value, ...codes] : codes;
  return (
    <label>
      {label}
      <select name={name} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{CHOOSE}</option>
        {options.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>
    </label>
  );
}

function Text({
  label,
  name,
  value,
  decimal = false,
  onChange,
}: {
  label: string;
  name: string;
  value: string;
  decimal?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      {label}
      <input
        inputMode={decimal ? "decimal" : "text"}
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function BusFields({
  bus,
  index,
  flagged,
  onChange,
  onRemove,
}: RowProps & { bus: BusRow; onChange: (patch: Partial<BusRow>) => void }) {
  const field = (name: string) => `${bus.id}-${name}`;
  return (
    <Row kind="Bus" id={bus.id} index={index} flagged={flagged} onRemove={onRemove}>
      <Text
        label="Code"
        name={field("code")}
        value={bus.code}
        onChange={(code) => onChange({ code })}
      />
      <Text
        label="Name"
        name={field("name")}
        value={bus.name}
        onChange={(name) => onChange({ name })}
      />
      <Text
        label="Nominal voltage (V)"
        name={field("nominal-voltage")}
        value={bus.nominalVoltageV}
        decimal
        onChange={(nominalVoltageV) => onChange({ nominalVoltageV })}
      />
      <Choice
        label="Kind"
        name={field("kind")}
        value={bus.kind}
        options={busKindSchema.options}
        labels={BUS_KIND_LABELS}
        onChange={(kind) => onChange({ kind })}
      />
    </Row>
  );
}

function SourceFields({
  source,
  index,
  flagged,
  onChange,
  onRemove,
}: RowProps & { source: SourceRow; onChange: (patch: Partial<SourceRow>) => void }) {
  const field = (name: string) => `${source.id}-${name}`;
  return (
    <Row kind="Source" id={source.id} index={index} flagged={flagged} onRemove={onRemove}>
      <Text
        label="Code"
        name={field("code")}
        value={source.code}
        onChange={(code) => onChange({ code })}
      />
      <Choice
        label="Kind"
        name={field("kind")}
        value={source.kind}
        options={sourceKindSchema.options}
        labels={SOURCE_KIND_LABELS}
        onChange={(kind) => onChange({ kind })}
      />
      <Text
        label="Voltage (V)"
        name={field("voltage")}
        value={source.voltageV}
        decimal
        onChange={(voltageV) => onChange({ voltageV })}
      />
      {source.kind === "GENERATOR" || source.kind === "UPS" || source.kind === "PV" ? (
        <Text
          label={source.kind === "PV" ? "Rating (kW AC, optional)" : "Rating (kVA, optional)"}
          name={field("rating")}
          value={source.rating}
          decimal
          onChange={(rating) => onChange({ rating })}
        />
      ) : null}
    </Row>
  );
}

function TransformerFields({
  transformer,
  index,
  busCodes,
  flagged,
  onChange,
  onRemove,
}: RowProps & {
  transformer: TransformerRow;
  busCodes: string[];
  onChange: (patch: Partial<TransformerRow>) => void;
}) {
  const field = (name: string) => `${transformer.id}-${name}`;
  return (
    <Row kind="Transformer" id={transformer.id} index={index} flagged={flagged} onRemove={onRemove}>
      <Text
        label="Code"
        name={field("code")}
        value={transformer.code}
        onChange={(code) => onChange({ code })}
      />
      <Picker
        label="From bus"
        name={field("from-bus")}
        value={transformer.fromBus}
        codes={busCodes}
        onChange={(fromBus) => onChange({ fromBus })}
      />
      <Picker
        label="To bus"
        name={field("to-bus")}
        value={transformer.toBus}
        codes={busCodes}
        onChange={(toBus) => onChange({ toBus })}
      />
      <Text
        label="Rating (kVA)"
        name={field("rating")}
        value={transformer.ratingKva}
        decimal
        onChange={(ratingKva) => onChange({ ratingKva })}
      />
      <Text
        label="Primary voltage (V)"
        name={field("primary-voltage")}
        value={transformer.primaryV}
        decimal
        onChange={(primaryV) => onChange({ primaryV })}
      />
      <Text
        label="Secondary voltage (V)"
        name={field("secondary-voltage")}
        value={transformer.secondaryV}
        decimal
        onChange={(secondaryV) => onChange({ secondaryV })}
      />
      <DeviceGroup
        prefix={transformer.id}
        legend="Secondary incomer device (optional)"
        device={transformer.device}
        onChange={(device) => onChange({ device })}
      />
    </Row>
  );
}

function DeviceGroup({
  prefix,
  legend = "Device (optional)",
  device,
  onChange,
}: {
  prefix: string;
  legend?: string;
  device: DeviceDraft;
  onChange: (device: DeviceDraft) => void;
}) {
  return (
    <fieldset data-device-group="true">
      <legend>{legend}</legend>
      <Text
        label="Device code"
        name={`${prefix}-device-code`}
        value={device.code}
        onChange={(code) => onChange({ ...device, code })}
      />
      <Choice
        label="Device kind"
        name={`${prefix}-device-kind`}
        value={device.kind}
        options={deviceKindSchema.options}
        labels={DEVICE_KIND_LABELS}
        onChange={(kind) => onChange({ ...device, kind })}
      />
      <Text
        label="Device rating (A)"
        name={`${prefix}-device-rating`}
        value={device.rating}
        decimal
        onChange={(rating) => onChange({ ...device, rating })}
      />
    </fieldset>
  );
}

function CableGroup({
  prefix,
  cable,
  onChange,
}: {
  prefix: string;
  cable: CableDraft;
  onChange: (cable: CableDraft) => void;
}) {
  return (
    <fieldset data-cable-group="true">
      <legend>Cable (optional)</legend>
      <Text
        label="Cable code"
        name={`${prefix}-cable-code`}
        value={cable.code}
        onChange={(code) => onChange({ ...cable, code })}
      />
      <Text
        label="Cable length (m)"
        name={`${prefix}-cable-length`}
        value={cable.length}
        decimal
        onChange={(length) => onChange({ ...cable, length })}
      />
    </fieldset>
  );
}

function IncomerFields({
  incomer,
  index,
  busCodes,
  sourceCodes,
  flagged,
  onChange,
  onRemove,
}: RowProps & {
  incomer: IncomerRow;
  busCodes: string[];
  sourceCodes: string[];
  onChange: (patch: Partial<IncomerRow>) => void;
}) {
  const field = (name: string) => `${incomer.id}-${name}`;
  return (
    <Row kind="Incomer" id={incomer.id} index={index} flagged={flagged} onRemove={onRemove}>
      <Text
        label="Code"
        name={field("code")}
        value={incomer.code}
        onChange={(code) => onChange({ code })}
      />
      <Picker
        label="Source"
        name={field("source")}
        value={incomer.source}
        codes={sourceCodes}
        onChange={(source) => onChange({ source })}
      />
      <Picker
        label="To bus"
        name={field("to-bus")}
        value={incomer.toBus}
        codes={busCodes}
        onChange={(toBus) => onChange({ toBus })}
      />
      <DeviceGroup
        prefix={incomer.id}
        device={incomer.device}
        onChange={(device) => onChange({ device })}
      />
    </Row>
  );
}

function FeederFields({
  feeder,
  index,
  busCodes,
  loadCodes,
  flagged,
  onChange,
  onRemove,
}: RowProps & {
  feeder: FeederRow;
  busCodes: string[];
  loadCodes: string[];
  onChange: (patch: Partial<FeederRow>) => void;
}) {
  const field = (name: string) => `${feeder.id}-${name}`;
  return (
    <Row kind="Feeder" id={feeder.id} index={index} flagged={flagged} onRemove={onRemove}>
      <Text
        label="Code"
        name={field("code")}
        value={feeder.code}
        onChange={(code) => onChange({ code })}
      />
      <Choice
        label="Kind"
        name={field("kind")}
        value={feeder.kind}
        options={feederKindSchema.options}
        labels={FEEDER_KIND_LABELS}
        onChange={(kind) => onChange({ kind })}
      />
      <Picker
        label="From bus"
        name={field("from-bus")}
        value={feeder.fromBus}
        codes={busCodes}
        onChange={(fromBus) => onChange({ fromBus })}
      />
      <Choice
        label="Target kind"
        name={field("target-kind")}
        value={feeder.targetKind}
        options={["bus", "load"] as const}
        labels={TARGET_KIND_LABELS}
        onChange={(targetKind) => onChange({ targetKind: targetKind as SldTargetKind })}
      />
      {feeder.targetKind === "bus" ? (
        <Picker
          label="Target bus"
          name={field("to-bus")}
          value={feeder.toBus}
          codes={busCodes}
          onChange={(toBus) => onChange({ toBus })}
        />
      ) : null}
      {feeder.targetKind === "load" ? (
        <Picker
          label="Target load"
          name={field("to-load")}
          value={feeder.toLoad}
          codes={loadCodes}
          onChange={(toLoad) => onChange({ toLoad })}
        />
      ) : null}
      <DeviceGroup
        prefix={feeder.id}
        device={feeder.device}
        onChange={(device) => onChange({ device })}
      />
      <CableGroup
        prefix={feeder.id}
        cable={feeder.cable}
        onChange={(cable) => onChange({ cable })}
      />
    </Row>
  );
}

function LoadFields({
  load,
  index,
  flagged,
  onChange,
  onRemove,
}: RowProps & { load: LoadRow; onChange: (patch: Partial<LoadRow>) => void }) {
  const field = (name: string) => `${load.id}-${name}`;
  return (
    <Row kind="Load" id={load.id} index={index} flagged={flagged} onRemove={onRemove}>
      <Text
        label="Code"
        name={field("code")}
        value={load.code}
        onChange={(code) => onChange({ code })}
      />
      <Choice
        label="Kind"
        name={field("kind")}
        value={load.kind}
        options={loadKindSchema.options}
        labels={LOAD_KIND_LABELS}
        onChange={(kind) => onChange({ kind })}
      />
      <Text
        label="Rated power (kW)"
        name={field("rated-power")}
        value={load.ratedKw}
        decimal
        onChange={(ratedKw) => onChange({ ratedKw })}
      />
    </Row>
  );
}

export default SldEditorForm;
